import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import PersonCell from '../../../components/ui/PersonCell';

const useBenefitDependentApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const paramsToUse = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = paramsToUse;
    setFetching(true);
    api.get(`/v1/benefit-dependents?${new URLSearchParams(paramsToUse)}`)
      .then((res) => {
        const result = res.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (formData) => {
    setFetching(true);
    try {
      await api.post('/v1/benefit-dependents', formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const update = useCallback(async (id, formData) => {
    setFetching(true);
    try {
      await api.put(`/v1/benefit-dependents/${id}`, formData);
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      setFetching(false);
      return false;
    }
  }, []);

  const _delete = useCallback(async (ids) => {
    try {
      for (const id of ids) {
        await api.delete(`/v1/benefit-dependents/${id}`);
      }
      setRefreshTrigger((p) => p + 1);
      return true;
    } catch {
      return false;
    }
  }, []);

  const typeOptions = useMemo(() => [
    { id: 'spouse', name: t('benefitDependent.typeSpouse') },
    { id: 'child', name: t('benefitDependent.typeChild') },
    { id: 'parent', name: t('benefitDependent.typeParent') },
    { id: 'beneficiary', name: t('benefitDependent.typeBeneficiary') },
  ], [t]);

  const typeLabels = useMemo(() => ({
    spouse: t('benefitDependent.typeSpouse'),
    child: t('benefitDependent.typeChild'),
    parent: t('benefitDependent.typeParent'),
    beneficiary: t('benefitDependent.typeBeneficiary'),
  }), [t]);

  const searchEmployees = useCallback(async (q) => {
    const params = q ? { 'firstName[like]': q, pageSize: 20 } : { pageSize: 20 };
    const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
    const result = res.data?.data;
    const items = Array.isArray(result) ? result : (result?.data || []);
    return items.map((e) => ({
      id: e.id,
      name: `${e.firstName || ''} ${e.lastName || ''}`.trim(),
      profilePicture: e.profilePicture,
    }));
  }, []);

  const getEmployeeById = useCallback(async (id) => {
    if (!id) return null;
    try {
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e ? { id: e.id, name: `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture } : null;
    } catch {
      return null;
    }
  }, []);

  // Mask CitizenId by default — IK can reveal via row hover/click on the value
  const maskCitizenId = (v) => {
    if (!v) return '-';
    if (v.length <= 4) return '****';
    return `${'*'.repeat(v.length - 4)}${v.slice(-4)}`;
  };

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    { name: 'employeeFullName', label: t('benefitDependent.employee'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.employeeProfilePicture} />
      ), filter: false, sort: true } },
    {
      name: 'type', label: t('benefitDependent.type'),
      options: {
        filter: true, sort: true, filterType: 'dropdown',
        filterOptions: { defaultOp: 'eq', names: typeOptions.map(s => s.name), renderValue: (v) => v },
        customBodyRender: (v) => typeLabels[v] || v,
      },
    },
    { name: 'firstName', label: t('benefitDependent.firstName'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    { name: 'lastName', label: t('benefitDependent.lastName'), options: { filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' } } },
    {
      name: 'citizenId', label: t('benefitDependent.citizenId'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => maskCitizenId(v),
      },
    },
    {
      name: 'birthDate', label: t('benefitDependent.birthDate'),
      options: { filter: false, sort: true, customBodyRender: (v) => v ? new Date(v).toLocaleDateString('tr-TR') : '-' },
    },
    { name: 'status', label: t('benefitDependent.status'), options: { filter: false, sort: false } },
  ], [t, typeLabels, typeOptions, data]);

  const objectSchema = useMemo(() => [
    {
      name: 'employeeId', label: t('benefitDependent.employee'), type: 'asyncSelect', grid: 6, required: true,
      onSearch: searchEmployees, onGetById: getEmployeeById,
    },
    {
      name: 'type', label: t('benefitDependent.type'), type: 'select', grid: 6, required: true,
      options: typeOptions, idKey: 'id', labelKey: 'name',
    },
    { name: 'firstName', label: t('benefitDependent.firstName'), type: 'text', grid: 6, required: true },
    { name: 'lastName', label: t('benefitDependent.lastName'), type: 'text', grid: 6, required: true },
    { name: 'citizenId', label: t('benefitDependent.citizenId'), type: 'text', grid: 6 },
    { name: 'birthDate', label: t('benefitDependent.birthDate'), type: 'date', grid: 6, required: true },
    {
      name: 'relationshipDocumentUrl', label: t('benefitDependent.documentUrl'), type: 'pictureUpload', grid: 12,
      shape: 'square', accept: 'image/*,application/pdf',
      validTypes: ['image/png', 'image/jpeg', 'image/webp', 'application/pdf'],
      maxSize: 5,
    },
  ], [t, searchEmployees, getEmployeeById, typeOptions]);

  return { objectSchema, columns, data, fetching, pagination, create, update, _delete };
};

export default useBenefitDependentApi;
